import { Headline, Text, TextButton } from '@oneaudi/unified-web-components';
import { useAtom } from 'jotai';
import Markdown from 'markdown-to-jsx';
import { useCallback } from 'react';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { mapTrackingIncentives } from '~/app/tracking/map-tracking-incentives';
import { Dialog } from '~/components/dialog/dialog';
import { TermsAndConditionsContentStyle } from '~/components/dialog/terms-and-conditions-dialog/styles/terms-and-conditions-dialog-style';
import { Incentive } from '~/graphql/__generated__/graphql';
import { state } from '~/storage';
import { formatDate } from '~/utils/incentives';

type DialogData = {
	incentive: Incentive;
	onSelect: (incentive: Incentive) => void;
	showDialogButtons: boolean;
};

export function useTermsAndConditionsDialog() {
	const [dialog, setDialog] = useAtom(state.termsAndConditionsDialog);

	return {
		data: dialog,
		showTermsAndConditionsDialog: useCallback(
			(data: DialogData) => {
				setDialog(data);
				// Tracking of the 'terms & conditions' layer open
				appTrackingService.incentivesClickEvent({
					eventName: "tacticals - click to open 'terms & conditions' layer",
					componentName: 'configurator-configuration-overview',
					label: 'Terms & Conditions',
					elementName: 'text link',
					relatedIncentive: mapTrackingIncentives([data.incentive]),
				});
			},
			[setDialog],
		),
		open: !!dialog,
		close: useCallback(() => {
			setDialog(null);
		}, [setDialog]),
	};
}

export function TermsAndConditionsDialog() {
	const { open, close, data } = useTermsAndConditionsDialog();
	return (
		<Dialog open={open} onCloseButtonClick={close}>
			{data && <DialogContent data={data} onClose={close} />}
		</Dialog>
	);
}

function DialogContent({ data, onClose }: { data: DialogData; onClose: () => void }) {
	const { i18n } = useI18n();
	const { formatter, market, language } = useMarketConfig();

	const handleSelect = () => {
		if (data.incentive.status.selected) return;
		data.onSelect(data.incentive);
		onClose();
	};

	const absolutePrice = formatter.formatPrice(Math.abs(data.incentive.price.value));

	return (
		<TermsAndConditionsContentStyle>
			<Headline tag={'h2'} variant={{ type: 'headline', order: '1', weight: 'normal' }} className="title">
				{data.incentive.action.title}
			</Headline>
			<Row title={i18n('ngc.incentives.description')} value={`${data.incentive.action.description}.`} />
			{data.incentive.price.value !== 0 && <Row title={i18n('ngc.incentives.promotion')} value={absolutePrice} />}
			<Row
				title={i18n('ngc.incentives.promotionPeriod')}
				value={`${formatDate(data.incentive.validityPeriod.from || '', market, language)}-${formatDate(data.incentive.validityPeriod.to || '', market, language)}`}
			/>
			<Row title={i18n('ngc.incentives.eligibility')} value={data.incentive.customerGroup.description} markdown />
			<Row title={i18n('ngc.incentives.proof')} value={data.incentive.customerGroup.eligibility} markdown />
			<Row
				title={i18n('ngc.incentives.eligibilityVehicle')}
				value={data.incentive.vehicleEligibilityCheck}
				markdown
			/>
			<Row title={i18n('ngc.incentives.legalLimitation')} value={data.incentive.limitation} markdown />
			<div className={'btn-container'}>
				{data.showDialogButtons && (
					<TextButton variant="secondary" onClick={onClose}>
						{i18n('ngc.actions.cancel')}
					</TextButton>
				)}
				{data.showDialogButtons && (
					<TextButton variant="primary" disabled={data.incentive.status.selected} onClick={handleSelect}>
						{i18n('ngc.actions.apply')}
					</TextButton>
				)}
			</div>
		</TermsAndConditionsContentStyle>
	);
}

const Row = ({ title, value, markdown }: { title: string; value?: string | null; markdown?: boolean }) => {
	if (!value) return <></>;
	return (
		<div className="row">
			<Text variant={{ type: 'copy', order: '1', style: 'bold' }}>{title}</Text>
			<Text
				variant={{ type: 'copy', order: '1', style: 'normal' }}
				className={markdown ? 'activate-list-view' : ''}>
				{value && markdown ? <Markdown>{value}</Markdown> : value}
			</Text>
		</div>
	);
};
